import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Power, Wallet, Route, Clock, Star, MapPin, Loader2, ShieldAlert, Check, X, Navigation, Car, UtensilsCrossed, Bike, Phone, Store, PackageCheck, Info, BadgeCheck, Banknote, Smartphone, CheckCircle2, Package, Camera, PenLine, AlertTriangle, Snowflake } from "lucide-react";
import GoodsProofDialog from "../../components/app/GoodsProofDialog";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PageHeader, Section, StatCard, StatusBadge } from "../../components/app/ui";
import PendingTasks from "../../components/app/PendingTasks";
import PayoutReminder from "../../components/app/PayoutReminder";
import DriverReadinessBanner from "../../components/app/DriverReadinessBanner";
import SampleRequestCard from "../../components/app/SampleRequestCard";
import { ArrivedWaiting } from "../../components/app/ArrivedWaiting";
import RideMap from "../../components/app/RideMap";
import InAppNav from "../../components/app/InAppNav";
import useRoadRoute from "../../components/app/useRoadRoute";
import TrafficLegend from "../../components/app/TrafficLegend";
import { useAuth } from "../../context/AuthContext";
import { driverApi, ridesApi, foodApi, configApi, inr2, errorMessage } from "../../lib/api";
import { RingModal } from "../../components/app/RingModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Textarea } from "../../components/ui/textarea";
import { driverType, categoryOf, isPrivateCarCategory } from "../../lib/driver";
import { DEFAULT_CENTER } from "../../lib/constants";

const MODE_KEY = "kt_driver_mode"; // rides | food | both

const MODES = [
  { value: "rides", label: "Rides", icon: Car },
  { value: "food", label: "Deliveries", icon: UtensilsCrossed },
  { value: "both", label: "Both", icon: Bike },
];

function KindBadge({ kind }) {
  return kind === "food" ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2.5 py-1 text-xs font-semibold text-brand" data-testid="kind-food"><UtensilsCrossed className="h-3 w-3" /> Food delivery</span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-graphite-50 px-2.5 py-1 text-xs font-semibold text-graphite" data-testid="kind-ride"><Car className="h-3 w-3" /> Passenger ride</span>
  );
}

function Route2({ a, b, aLabel, bLabel }) {
  return (
    <div className="space-y-2 text-sm">
      <p className="flex gap-2"><span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-graphite" /><span><span className="block text-[10px] font-semibold uppercase tracking-wider text-ink-muted">{aLabel}</span>{a}</span></p>
      <p className="flex gap-2"><span className="mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full bg-brand" /><span><span className="block text-[10px] font-semibold uppercase tracking-wider text-ink-muted">{bLabel}</span>{b}</span></p>
    </div>
  );
}

export default function DriverDashboard() {
  const { user } = useAuth();
  const verified = user?.verification_status === "verified";
  const deliveryOnly = driverType(user) === "delivery";
  const isPrivateCar = isPrivateCarCategory(categoryOf(user));
  const foodEnabled = deliveryOnly || (user?.services || []).includes("food");
  // Online status is controlled ONLY by the top header toggle (AppLayout OnlineToggle),
  // which sets user.is_online. The dashboard reads it as the single source of truth.
  const online = !!user?.is_online;
  const [mode, setMode] = useState(() => localStorage.getItem(MODE_KEY) || "both");
  const [earnings, setEarnings] = useState(null);
  const [pos, setPos] = useState(DEFAULT_CENTER);
  const [incoming, setIncoming] = useState(null); // { kind: "ride" | "food", ... }
  const [job, setJob] = useState(null); // accepted request in progress
  const [queuedJob, setQueuedJob] = useState(null); // pre-accepted next job (queued behind the active one)
  const [heatPoints, setHeatPoints] = useState([]);
  const [forecast, setForecast] = useState({ confidence: 1, busy_soon: false, nearest_hotspot_km: null, hotspot: null, extra_earnings: 0, extra_trips: 0 });
  const [timeline, setTimeline] = useState([]);
  const [timelinePeak, setTimelinePeak] = useState(null);
  const [proofOpen, setProofOpen] = useState(false);
  const [phase, setPhase] = useState("to_pickup"); // to_pickup | on_trip
  const [endedRide, setEndedRide] = useState(null); // ride in payment_pending awaiting settlement
  const [navDest, setNavDest] = useState(null); // in-app turn-by-turn navigation target
  const [unsettled, setUnsettled] = useState([]); // stale payment_pending trips out of the active slot
  const [payBusy, setPayBusy] = useState(false);
  const [startCode, setStartCode] = useState("");
  const [noShowBusy, setNoShowBusy] = useState(false);
  const [dropCode, setDropCode] = useState("");
  const [codeOverride, setCodeOverride] = useState(false);
  const [tollInput, setTollInput] = useState("");
  const [waitInput, setWaitInput] = useState("");
  const [chargesBusy, setChargesBusy] = useState(false);
  const [cancelReasonOpen, setCancelReasonOpen] = useState(false);
  const saveIntercityCharges = async () => {
    if (!job?.ride_id) return;
    setChargesBusy(true);
    try {
      const r = await ridesApi.intercityCharges(job.ride_id, { toll: Number(tollInput) || 0, waiting_min: Number(waitInput) || 0 });
      setJob({ ...job, ...r, kind: "ride", request_id: r.ride_id });
      toast.success("Tolls & waiting added to the fare");
    } catch (e) { toast.error(errorMessage(e, "Could not add charges")); }
    finally { setChargesBusy(false); }
  };
  const [recent, setRecent] = useState([]);
  const pollRef = useRef(null);
  const flip = useRef(0);

  const effectiveMode = deliveryOnly ? "food" : foodEnabled ? mode : "rides";

  // Normalise a backend delivery card to the shape the dashboard UI expects.
  const mapDelivery = (o) => ({
    ...o, kind: "food", request_id: o.order_id,
    fare: o.delivery_fee,
    order_value: o.total,
    payment: o.payment_method === "cash" ? "cod" : "paid",
    pickup_address: o.restaurant_address || o.restaurant_name,
    drop_address: o.delivery_address,
  });

  const loadEarnings = useCallback(() => driverApi.earningsSummary().then((s) => { setEarnings(s); setRecent(s.recent || []); }).catch(() => {}), []);
  useEffect(() => { loadEarnings(); }, [loadEarnings]);

  // Restore an in-progress food delivery from the backend (survives refresh).
  useEffect(() => {
    if (foodEnabled) {
      driverApi.deliveries().then((d) => {
        const a = (d.active || []).find((o) => !o.queued);
        if (a) { setJob(mapDelivery(a)); setPhase(a.status === "out_for_delivery" ? "on_trip" : "to_pickup"); }
      }).catch(() => {});
    }
    // Restore a pre-accepted next job (ride or food) queued behind the active one.
    driverApi.nextJob().then((n) => {
      if (n.ride) setQueuedJob({ ...n.ride, kind: "ride", request_id: n.ride.ride_id });
      else if (n.order) setQueuedJob(mapDelivery(n.order));
      else setQueuedJob(null);
    }).catch(() => {});
  }, [foodEnabled]);

  // Restore the active / awaiting-payment ride from the backend so a refresh (or ending a
  // ride then reloading) never loses the trip the driver still has to settle & complete.
  useEffect(() => {
    driverApi.activeRide().then((a) => {
      setUnsettled(a?.unsettled || []);
      const r = a?.ride;
      if (!r) return;
      if (r.status === "payment_pending") setEndedRide(r);
      else { setJob({ ...r, kind: "ride", request_id: r.ride_id }); setPhase(r.status === "in_progress" ? "on_trip" : "to_pickup"); }
    }).catch(() => {});
  }, []);

  useEffect(() => {
    navigator.geolocation?.getCurrentPosition((p) => setPos({ lat: p.coords.latitude, lng: p.coords.longitude }), () => {}, { timeout: 5000 });
  }, []);

  // Keep the driver's location fresh while online so dispatch keeps sending jobs
  // (the backend stops serving drivers whose location is stale/off).
  useEffect(() => {
    if (!online || !navigator.geolocation) return undefined;
    const post = () => navigator.geolocation.getCurrentPosition(
      (p) => { setPos({ lat: p.coords.latitude, lng: p.coords.longitude }); driverApi.postLocation(p.coords.latitude, p.coords.longitude).catch(() => {}); },
      () => {}, { timeout: 8000, enableHighAccuracy: false },
    );
    post();
    const t = setInterval(post, 60000);
    return () => clearInterval(t);
  }, [online]);

  // Next-hour demand forecast — predicted request hotspots (shown when idle & online).
  useEffect(() => {
    if (!online) { setHeatPoints([]); setForecast({ confidence: 1, busy_soon: false, nearest_hotspot_km: null }); return undefined; }
    let ignore = false;
    const c = pos || DEFAULT_CENTER;
    const load = () => configApi.demandForecast(c.lat, c.lng, 8).then((d) => {
      if (ignore) return;
      setHeatPoints(d.points || []);
      setForecast({ confidence: typeof d.confidence === "number" ? d.confidence : 1, busy_soon: !!d.busy_soon, nearest_hotspot_km: d.nearest_hotspot_km ?? null, hotspot: d.hotspot ?? null, extra_earnings: d.extra_earnings || 0, extra_trips: d.extra_trips || 0 });
    }).catch(() => {});
    load();
    configApi.demandTimeline(c.lat, c.lng, 8).then((t) => { if (!ignore) { setTimeline(t.slots || []); setTimelinePeak(t.peak_hour ?? null); } }).catch(() => {});
    const t = setInterval(load, 60000);
    return () => { ignore = true; clearInterval(t); };
  }, [online, pos]);

  // Poll for incoming requests while online. Keep polling during an active job so the
  // driver can pre-accept ONE next job; stop once a next job is queued.
  useEffect(() => {
    clearInterval(pollRef.current);
    if (!online || queuedJob) { setIncoming(null); return undefined; }
    const tick = async () => {
      const wantFood = effectiveMode === "food" || (effectiveMode === "both" && flip.current % 2 === 1);
      flip.current += 1;
      try {
        if (wantFood) {
          const dres = await driverApi.deliveries();
          const d = (dres.available || [])[0];
          if (d) {
            setIncoming(mapDelivery(d));
            return;
          }
          if (effectiveMode === "food") { setIncoming(null); return; }
        }
        const r = await driverApi.rideRequests(pos.lat, pos.lng);
        const req = r && r[0];
        setIncoming(req ? { ...req, kind: "ride", request_id: req.ride_id } : null);
      } catch { /* keep previous */ }
    };
    tick();
    pollRef.current = setInterval(tick, 12000);
    return () => clearInterval(pollRef.current);
  }, [online, job, queuedJob, pos, effectiveMode]);

  const changeMode = (m) => { setMode(m); localStorage.setItem(MODE_KEY, m); setIncoming(null); flip.current = m === "food" ? 1 : 0; };

  const accept = async () => {
    const inc = incoming;
    if (!inc) return;
    const busy = !!job;
    if (inc.kind === "food") {
      try {
        const o = await driverApi.claimDelivery(inc.order_id);
        const mapped = mapDelivery(o);
        setIncoming(null);
        if (busy) { setQueuedJob(mapped); toast.success("Next delivery lined up — you'll head there after this trip"); }
        else { setJob(mapped); setPhase("to_pickup"); toast.success(`Claimed! Head to ${o.restaurant_name} to pick up`); }
      } catch (e) {
        setIncoming(null);
        toast.error(e?.response?.status === 409 ? (e.response.data?.detail || "That delivery was already claimed by another partner") : errorMessage(e));
      }
      return;
    }
    try {
      const r = await ridesApi.accept(inc.ride_id, pos ? { lat: pos.lat, lng: pos.lng } : undefined);
      const mapped = { ...inc, ...(r || {}), kind: "ride", request_id: inc.ride_id };
      setIncoming(null);
      if (busy) { setQueuedJob(mapped); toast.success("Next ride lined up — you'll head there after this trip"); }
      else { setJob(mapped); setPhase("to_pickup"); toast.success(`Ride accepted — heading to ${inc.pickup_address}`); }
    } catch (e) {
      setIncoming(null);
      toast.error(e?.response?.status === 409 ? (e.response.data?.detail || "That ride was already taken by another driver") : errorMessage(e));
    }
  };
  // When the current job finishes/cancels, promote the pre-accepted next job to active.
  const promoteNext = () => {
    if (queuedJob) {
      setJob(queuedJob);
      setPhase(queuedJob.kind === "food" && queuedJob.status === "out_for_delivery" ? "on_trip" : "to_pickup");
      setQueuedJob(null);
      toast.success("Starting your next job");
    } else {
      setJob(null); setPhase("to_pickup");
    }
  };
  const decline = async () => {
    const inc = incoming;
    setIncoming(null);
    if (inc?.kind === "ride" && inc.ride_id) { try { await ridesApi.decline(inc.ride_id); } catch { /* ignore */ } }
    toast("Request skipped");
  };

  // A rider can cancel right up until the trip starts. Poll the active ride so a customer
  // cancellation clears the driver's stale trip card on its own (a push isn't guaranteed).
  useEffect(() => {
    if (!job || job.kind !== "ride" || phase === "on_trip") return undefined;
    const t = setInterval(async () => {
      try {
        const r = await ridesApi.get(job.request_id);
        if (!r || r.status === "cancelled") {
          toast.info(r?.rider_cancel_reason ? `Rider cancelled — ${r.rider_cancel_reason}${r.rider_cancel_note ? ` (${r.rider_cancel_note})` : ""}` : "The rider cancelled this trip");
          promoteNext();
        }
      } catch { /* ignore */ }
    }, 8000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.request_id, job?.kind, phase]);

  const markRideArrived = async () => {
    try { await ridesApi.arrived(job.ride_id); const jr = await ridesApi.get(job.ride_id).catch(() => null); if (jr) setJob(jr); toast.success("Rider notified you've arrived"); }
    catch (e) { toast.error(errorMessage(e)); }
  };
  const markNoShow = async () => {
    if (!window.confirm("Report the rider as a no-show and cancel this ride? A cancellation fee (₹20 + waiting time) will be billed to the rider on their next ride.")) return;
    setNoShowBusy(true);
    try { const res = await ridesApi.noShowCancel(job.ride_id); toast.success(res?.noshow_auto_waived ? "No fee charged — GPS showed you weren't at the pickup" : "Ride cancelled — no-show fee billed to the rider"); promoteNext(); loadEarnings(); }
    catch (e) { toast.error(errorMessage(e)); }
    finally { setNoShowBusy(false); }
  };
  const markFoodArrived = async () => {
    try { const card = await foodApi.driverArrived(job.order_id); setJob(mapDelivery(card)); toast.success("Customer notified you've arrived"); }
    catch (e) { toast.error(errorMessage(e)); }
  };

  const startRideTrip = async () => {    try {
      await ridesApi.start(job.ride_id, startCode.trim());
      setPhase("on_trip"); setStartCode("");
      toast.success("Code verified — trip started. Drive safe!");
    } catch (e) {
      toast.error(e?.response?.status === 400 ? (e.response.data?.detail || "Incorrect code") : errorMessage(e));
    }
  };

  const collectGoodsCashPickup = async () => {
    try {
      const r = await ridesApi.cashCollected(job.ride_id);
      setJob({ ...job, cash_collected: true, cash_collected_at: r?.cash_collected_at });
      toast.success("Estimated cash collected from sender");
    } catch (e) { toast.error(errorMessage(e, "Could not confirm cash")); }
  };

  const advance = async () => {
    const coords = pos ? { lat: pos.lat, lng: pos.lng } : undefined;
    if (phase === "to_pickup") {
      if (job.kind === "food") {
        try { await foodApi.orderStatus(job.order_id, "out_for_delivery", undefined, coords); setPhase("on_trip"); toast.success("Picked up. Deliver to the customer."); }
        catch (e) { toast.error(e?.response?.status === 400 ? (e.response.data?.detail || "Move closer to the restaurant") : errorMessage(e)); }
      } else {
        try { await ridesApi.status(job.ride_id, "in_progress"); } catch { /* ignore */ } setPhase("on_trip"); toast.success("Trip started. Drive safe!");
      }
      return;
    }
    try {
      if (job.kind === "food") {
        await foodApi.orderStatus(job.order_id, "delivered", undefined, coords);
        await driverApi.logEarning({ amount: job.delivery_fee || job.fare, kind: "food", ref_id: job.order_id, distance_km: job.distance_km || 0 });
        toast.success(`Delivered! ${inr2(job.delivery_fee || job.fare)} added to earnings`);
        promoteNext(); loadEarnings();
      } else {
        // End the ride (100 m drop geofence); rider then settles the fare (cash/online).
        const r = await ridesApi.end(job.ride_id, coords);
        setEndedRide(r);
        toast.success(r?.geofence_warning ? r.geofence_warning : "Ride ended — waiting for the rider to pay");
      }
    } catch (e) { toast.error(e?.response?.status === 400 ? (e.response.data?.detail || "Move within 100 m of the drop-off") : errorMessage(e)); }
  };

  // Poll the ended ride so online payment shows up, then let the driver complete it.
  useEffect(() => {
    if (!endedRide || endedRide.payment_status === "paid" || endedRide.cash_collected) return;
    const t = setInterval(() => {
      ridesApi.get(endedRide.ride_id).then((r) => { if (r) setEndedRide(r); }).catch(() => {});
    }, 5000);
    return () => clearInterval(t);
  }, [endedRide]);

  const markCashCollected = async () => {
    if (!endedRide) return;
    setPayBusy(true);
    try {
      const r = await ridesApi.cashCollected(endedRide.ride_id);
      setEndedRide(r);
      toast.success("Cash collected — you can complete the trip now");
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setPayBusy(false); }
  };

  const finalizeRide = async (collectCashFirst) => {
    if (!endedRide) return;
    setPayBusy(true);
    try {
      let r = endedRide;
      if (collectCashFirst) { r = await ridesApi.cashCollected(endedRide.ride_id); setEndedRide(r); }
      const extra = endedRide.purpose === "goods" ? { drop_code: dropCode.trim(), code_override: codeOverride } : {};
      await ridesApi.status(endedRide.ride_id, "completed", extra);
      await driverApi.logEarning({ amount: r.fare, kind: "ride", ref_id: r.ride_id, distance_km: r.distance_km });
      toast.success(`Trip complete. ${inr2(r.fare)} added to earnings`);
      setEndedRide(null); setDropCode(""); setCodeOverride(false); promoteNext(); loadEarnings();
    } catch (e) { toast.error(errorMessage(e)); }
    finally { setPayBusy(false); }
  };

  const cancelJob = async () => {
    if (job?.kind === "food") { toast("A claimed delivery can't be dropped — complete pickup & delivery."); return; }
    if (phase === "on_trip") { toast.error("A trip in progress can't be cancelled. Please contact support to cancel or complete it."); return; }
    setCancelReasonOpen(true);
  };
  const submitCancelJob = async (reason, note) => {
    if (job?.ride_id) { try { await ridesApi.driverRelease(job.ride_id, reason ? { reason, note: note || "" } : {}); } catch (e) { toast.error(errorMessage(e, "Could not cancel")); return; } }
    setCancelReasonOpen(false);
    promoteNext(); toast("Ride released — finding another driver");
  };

  const today = earnings?.today || {};
  const focus = job || incoming;
  const isFood = focus?.kind === "food";

  const driverTarget = job ? (phase === "to_pickup" ? { lat: job.pickup_lat, lng: job.pickup_lng } : { lat: job.drop_lat, lng: job.drop_lng }) : null;
  const driverRoad = useRoadRoute(pos, driverTarget, !!job && !!pos);
  const driverEta = driverRoad.durationMin != null ? Math.max(1, Math.round(driverRoad.durationMin)) : null;

  return (
    <>
      <PageHeader
        title={`Good day, ${user?.name?.split(" ")[0] || "driver"}`}
        description="Use the Online switch in the top bar to receive ride and delivery requests."
        actions={
          <div className={`flex items-center gap-2 rounded-full border px-4 py-2 ${online ? "border-graphite-200 bg-graphite-50" : "border-surface-300 bg-white"}`} data-testid="online-status-chip">
            <Power className={`h-4 w-4 ${online ? "text-graphite" : "text-ink-muted"}`} />
            <span className="text-sm font-semibold text-ink">{online ? "Online" : "Offline"}</span>
          </div>
        }
      />
      {!online && (
        <div className="mb-6 flex items-center gap-3 rounded-2xl border border-surface-300 bg-surface-50 p-4 text-sm text-ink-soft" data-testid="offline-hint">
          <Power className="h-4 w-4 text-ink-muted" /> You're offline. Flip the <span className="font-semibold text-ink">Online</span> switch at the top to start receiving requests.
        </div>
      )}
      <PendingTasks />
      <PayoutReminder />
      <DriverReadinessBanner showLink />
      {!verified && (
        <div className="mb-6 flex flex-wrap items-center gap-4 rounded-2xl border border-brand-200 bg-brand-50 p-5" data-testid="kyc-banner">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-white text-brand"><ShieldAlert className="h-5 w-5" /></span>
          <div className="min-w-0 flex-1"><p className="font-semibold text-ink">Complete verification to go online</p><p className="text-sm text-ink-soft">Upload your licence, RC and insurance. Status: <StatusBadge status={user?.verification_status} /></p></div>
          <Button asChild className="rounded-full bg-brand text-white hover:bg-brand-600"><Link to="/app/kyc" data-testid="kyc-banner-link">Upload documents</Link></Button>
        </div>
      )}
      {!deliveryOnly && user?.taxi_permit_number && (
        user?.taxi_verified ? (
          <div className="mb-6 flex items-center gap-2 rounded-2xl border border-graphite-200 bg-graphite-50 px-5 py-3" data-testid="taxi-verified-banner">
            <BadgeCheck className="h-5 w-5 text-graphite" />
            <p className="text-sm font-semibold text-graphite">Verified Taxi — your Commercial Taxi Permit is approved. You can accept passenger rides.</p>
          </div>
        ) : (
          <div className="mb-6 flex flex-wrap items-center gap-3 rounded-2xl border border-brand-200 bg-brand-50 px-5 py-3" data-testid="taxi-permit-banner">
            <ShieldAlert className="h-5 w-5 shrink-0 text-brand" />
            <p className="text-sm text-ink-soft">
              {user?.taxi_permit_status === "rejected"
                ? <>Your taxi permit was rejected{user?.taxi_permit_rejected_reason ? `: ${user.taxi_permit_rejected_reason}` : ""}. Update it in <Link to="/app/profile" className="font-semibold text-brand hover:underline">Profile</Link>.</>
                : user?.taxi_permit_expired
                ? <>Your taxi permit has expired. Renew it in <Link to="/app/profile" className="font-semibold text-brand hover:underline">Profile</Link> to keep carrying passengers.</>
                : <>Your Commercial Taxi Permit is pending admin approval. You can deliver food meanwhile — passenger rides unlock once approved.</>}
            </p>
          </div>
        )
      )}
      <div className="mb-6 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Today's earnings" value={inr2(today.total || 0)} hint={`${today.trips || 0} trips`} icon={Wallet} />
        <StatCard label="This week" value={inr2(earnings?.week?.total || 0)} hint={`${inr2(earnings?.week?.ride_amount || 0)} rides · ${inr2(earnings?.week?.food_amount || 0)} food`} icon={Route} tone="graphite" />
        <StatCard label="Hours online today" value={`${(earnings?.hours_online_today || 0).toFixed(1)} h`} icon={Clock} tone="ink" />
        <StatCard label="Rating" value={user?.rating ?? "5.0"} hint={user?.vehicle_type ? `${user.vehicle_type} · ${(user.services || []).join(", ") || "rides"}` : "Set your vehicle in Profile"} icon={Star} />
      </div>

      {/* Request type selector */}
      <div className="mb-6 flex flex-wrap items-center gap-3" data-testid="mode-selector">
        <span className="text-sm font-medium text-ink-soft">Accepting:</span>
        {isPrivateCar ? (
          <>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-4 py-1.5 text-sm font-semibold text-brand" data-testid="private-car-badge"><Car className="h-4 w-4" /> Private car hire · advance bookings</span>
            <p className="inline-flex items-center gap-1.5 text-xs text-ink-muted" data-testid="private-car-hint"><Info className="h-3.5 w-3.5" /> Manage your requests on the <Link to="/app/private-car-jobs" className="font-semibold text-brand hover:underline">Private car jobs</Link> page.</p>
          </>
        ) : deliveryOnly ? (
          <>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-4 py-1.5 text-sm font-semibold text-brand" data-testid="delivery-only-badge"><UtensilsCrossed className="h-4 w-4" /> Food deliveries only</span>
            <p className="inline-flex items-center gap-1.5 text-xs text-ink-muted" data-testid="delivery-only-hint"><Info className="h-3.5 w-3.5" /> You're a delivery partner on a two-wheeler. Add a taxi permit in <Link to="/app/profile" className="font-semibold text-brand hover:underline">Profile</Link> to carry passengers.</p>
          </>
        ) : (
          <>
            <div className="flex gap-1 rounded-full bg-surface-200 p-1">
              {MODES.map((m) => {
                const disabled = m.value !== "rides" && !foodEnabled;
                const active = effectiveMode === m.value;
                return (
                  <button key={m.value} type="button" disabled={disabled || !!job} onClick={() => changeMode(m.value)} className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors ${active ? "bg-white text-brand shadow-sm" : "text-ink-soft hover:text-ink"} disabled:cursor-not-allowed disabled:opacity-50`} data-testid={`mode-${m.value}`} aria-pressed={active}>
                    <m.icon className="h-4 w-4" /> {m.label}
                  </button>
                );
              })}
            </div>
            {!foodEnabled && (
              <p className="inline-flex items-center gap-1.5 text-xs text-ink-muted" data-testid="food-disabled-hint"><Info className="h-3.5 w-3.5" /> Add "Food delivery" in <Link to="/app/profile" className="font-semibold text-brand hover:underline">Profile</Link> to also accept deliveries.</p>
            )}
          </>
        )}
      </div>

      <div className="grid gap-6 lg:grid-cols-[440px_1fr]">
        <div className="space-y-6">
          {!job && unsettled.length > 0 ? (
            <Section title="Trips awaiting settlement" description="These ended but weren't settled — finish them so your earnings are logged. They no longer block new jobs.">
              <div className="space-y-2" data-testid="unsettled-trips">
                {unsettled.map((r) => (
                  <div key={r.ride_id} className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid={`unsettled-${r.ride_id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-ink">{r.drop_address || "Trip"}</p>
                      <p className="text-xs text-ink-soft">Collect {inr2(r.fare)} · {r.payment_method === "razorpay" ? "online" : "cash"}</p>
                    </div>
                    <Button onClick={() => { setJob({ ...r, kind: "ride", request_id: r.ride_id }); setPhase("on_trip"); setEndedRide(r); setUnsettled((u) => u.filter((x) => x.ride_id !== r.ride_id)); }} className="rounded-full bg-graphite text-white hover:bg-graphite-600" data-testid={`unsettled-settle-${r.ride_id}`}>Settle now</Button>
                  </div>
                ))}
              </div>
            </Section>
          ) : null}
          {job ? (
            <>
            <Section title={isFood ? (phase === "to_pickup" ? "Pick up from restaurant" : "Deliver to customer") : (phase === "to_pickup" ? "Heading to pickup" : "Trip in progress")} actions={<KindBadge kind={job.kind} />}>
              <div className="space-y-4" data-testid={isFood ? "active-delivery" : "active-trip"}>
                {isFood ? (
                  <>
                    <div className="flex items-center gap-3">
                      {job.restaurant_image ? <img src={job.restaurant_image} alt="" className="h-12 w-12 rounded-xl object-cover" /> : <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand"><Store className="h-5 w-5" /></span>}
                      <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold text-ink">{job.restaurant_name}</p><p className="text-xs text-ink-soft">For {job.customer_name} · {job.payment === "cod" ? `Collect ${inr2(job.order_value)} cash` : "Prepaid"}</p></div>
                      <p className="text-xl font-bold text-ink">{inr2(job.fare)}</p>
                    </div>
                    <Route2 a={job.pickup_address} b={job.drop_address} aLabel="Restaurant" bLabel="Customer" />
                    {phase === "to_pickup" && job.ready_eta_at && (
                      <p className="inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-bold text-amber-800" data-testid="food-ready-eta">
                        <Store className="h-3.5 w-3.5" /> {job.status === "ready" ? "Food is ready for pickup" : `Food ready in ~${Math.max(0, Math.round((new Date(job.ready_eta_at).getTime() - Date.now()) / 60000))} min`}
                      </p>
                    )}
                    {job.items?.length > 0 && <ul className="flex flex-wrap gap-2 text-xs">{job.items.map((i, idx) => <li key={idx} className="rounded-full bg-surface-100 px-2.5 py-1 text-ink">{i.qty} × {i.name}</li>)}</ul>}
                    <div className="flex items-center gap-2 rounded-xl bg-surface-100 px-3 py-2 text-xs text-ink-soft">
                      <span className={`flex h-6 w-6 items-center justify-center rounded-full ${phase === "to_pickup" ? "bg-brand text-white" : "bg-graphite text-white"}`}>{phase === "to_pickup" ? "1" : <Check className="h-3.5 w-3.5" />}</span> Pick up
                      <span className="h-px flex-1 bg-surface-300" />
                      <span className={`flex h-6 w-6 items-center justify-center rounded-full ${phase === "on_trip" ? "bg-brand text-white" : "bg-surface-300 text-ink-muted"}`}>2</span> Deliver
                    </div>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between"><div><p className="text-sm font-semibold text-ink">{job.rider_name}</p><p className="text-xs text-ink-soft">Customer rating {job.rider_rating}</p></div><p className="text-xl font-bold text-ink">{inr2(job.fare)}</p></div>
                    <Route2 a={job.pickup_address} b={job.drop_address} aLabel="Pickup" bLabel="Drop" />
                    {job.purpose === "goods" && (
                      <div className="space-y-1.5 rounded-xl bg-brand-50 p-3" data-testid="job-goods-info">
                        <p className="flex items-center gap-1.5 text-sm font-bold text-brand"><Package className="h-4 w-4" /> Goods delivery{job.weight_kg ? ` · ${job.weight_kg} kg` : ""}</p>
                        {job.receiver_name && <p className="text-xs text-ink">Receiver: <span className="font-semibold">{job.receiver_name}</span></p>}
                        {(job.payment_method || "cash") !== "cash" && job.payment_status === "paid" && (
                          <p className="inline-flex items-center gap-1.5 rounded-lg bg-green-100 px-2.5 py-1 text-xs font-bold text-green-700" data-testid="job-paid-online"><Smartphone className="h-3.5 w-3.5" /> Customer Paid Online</p>
                        )}
                        {job.package_photo_url && <img src={job.package_photo_url} alt="Package" className="h-28 w-28 rounded-lg border border-brand-200 object-cover" data-testid="job-package-photo" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
                        {job.delivery_instructions && <p className="text-xs text-ink">Instructions: {job.delivery_instructions}</p>}
                        {job.cod_payer === "receiver" && job.cod_amount && (
                          <p className="flex items-center gap-1.5 rounded-lg bg-amber-100 px-2.5 py-1.5 text-xs font-bold text-amber-800" data-testid="job-cod"><Banknote className="h-3.5 w-3.5" /> Collect {inr2(job.cod_amount)} cash from the receiver</p>
                        )}
                        <p className="text-[11px] italic text-ink-soft">You only pick up & drop the package. No loading/unloading. Not responsible for contents or damage.</p>
                        <div className="flex flex-wrap items-center gap-2">
                          {job.receiver_phone && <a href={`tel:${job.receiver_phone}`} className="inline-flex items-center gap-1.5 rounded-full bg-brand px-3 py-1.5 text-xs font-semibold text-white" data-testid="job-call-receiver"><Phone className="h-3.5 w-3.5" /> Call receiver</a>}
                          <label className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-xs font-bold ${job.pickup_photo_id ? "border-brand bg-brand text-white" : "border-brand text-brand"}`} data-testid="job-pickup-photo">
                            <Camera className="h-3.5 w-3.5" /> {job.pickup_photo_id ? "Pickup photo ✓" : "Pickup photo"}
                            <input type="file" accept="image/*" capture="environment" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; try { const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).replace(/^data:image\/\w+;base64,/, "")); r.onerror = rej; r.readAsDataURL(f); }); await ridesApi.pickupPhoto(job.ride_id, { file_data: b64, mime: f.type || "image/jpeg" }); toast.success("Pickup photo saved"); const jr = await ridesApi.get(job.ride_id).catch(() => null); if (jr) setJob(jr); } catch { toast.error("Could not save photo"); } }} />
                          </label>
                          <button type="button" onClick={() => setProofOpen(true)} className={`inline-flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-xs font-bold ${(job.proof_photo_id || job.proof_signature_id) ? "border-brand bg-brand text-white" : "border-brand text-brand"}`} data-testid="job-delivery-proof"><PenLine className="h-3.5 w-3.5" /> {(job.proof_photo_id || job.proof_signature_id) ? "Delivery proof ✓" : "Delivery proof"}</button>
                        </div>
                      </div>
                    )}
                  </>
                )}
                <p className="text-xs text-ink-muted" data-testid="driver-eta">{driverEta != null ? `~${driverEta} min ${phase === "to_pickup" ? "to pickup" : "to drop"}${driverRoad.distanceKm != null ? ` · ${driverRoad.distanceKm.toFixed(1)} km` : ""}` : (isFood ? (job.distance_km != null ? `${job.distance_km} km to customer` : "") : `${job.distance_km} km`)}</p>
                {isFood && phase === "on_trip" && job.order_id && (
                  <div className="space-y-1.5" data-testid="eta-ping">
                    <p className="text-xs font-semibold text-ink-soft">Ping the customer your ETA</p>
                    <div className="flex flex-wrap gap-2">
                      {[{ m: 0, l: "Arriving now" }, { m: 2, l: "2 min" }, { m: 5, l: "5 min" }, { m: 10, l: "10 min" }].map((p) => (
                        <button key={p.m} type="button" onClick={async () => { try { await foodApi.etaPing(job.order_id, p.m); toast.success(`Customer notified: ${p.l}`); } catch { toast.error("Could not send ETA"); } }} className="rounded-full border border-brand px-3 py-1.5 text-xs font-bold text-brand hover:bg-brand-50" data-testid={`eta-ping-${p.m}`}>{p.l}</button>
                      ))}
                    </div>
                    {job.delivery_arrived_at
                      ? <ArrivedWaiting arrivedAt={job.delivery_arrived_at} compact testid="food-arrived-waiting" />
                      : <Button onClick={markFoodArrived} variant="outline" className="rounded-full" data-testid="food-arrived-btn"><MapPin className="mr-2 h-4 w-4" /> I've arrived</Button>}
                  </div>
                )}
                {!isFood && phase === "to_pickup" && job.ride_id && (
                  <div className="space-y-1.5" data-testid="ride-eta-ping">
                    <p className="text-xs font-semibold text-ink-soft">Ping the rider your ETA to pickup</p>
                    <div className="flex flex-wrap gap-2">
                      {[{ m: 0, l: "Arriving now" }, { m: 2, l: "2 min" }, { m: 5, l: "5 min" }, { m: 10, l: "10 min" }].map((p) => (
                        <button key={p.m} type="button" onClick={async () => { try { await ridesApi.etaPing(job.ride_id, p.m); toast.success(`Rider notified: ${p.l}`); } catch { toast.error("Could not send ETA"); } }} className="rounded-full border border-brand px-3 py-1.5 text-xs font-bold text-brand hover:bg-brand-50" data-testid={`ride-eta-ping-${p.m}`}>{p.l}</button>
                      ))}
                    </div>
                    {job.arrived_at
                      ? <ArrivedWaiting arrivedAt={job.arrived_at} taxi={job.purpose !== "goods"} compact onNoShow={markNoShow} noShowBusy={noShowBusy} testid="ride-arrived-waiting" />
                      : <Button onClick={markRideArrived} variant="outline" className="rounded-full" data-testid="ride-arrived-btn"><MapPin className="mr-2 h-4 w-4" /> I've arrived</Button>}
                  </div>
                )}
                {driverTarget && (
                  <div className="flex items-center gap-2" data-testid="driver-nav-row">
                    <Button onClick={() => setNavDest(driverTarget)} className="flex-1 rounded-full bg-brand text-white hover:bg-brand-600" data-testid="driver-navigate-inapp"><Navigation className="mr-2 h-4 w-4" /> Navigate {phase === "to_pickup" ? "to pickup" : "to drop"}</Button>
                    <a href={`https://www.google.com/maps/dir/?api=1&origin=${pos.lat},${pos.lng}&destination=${driverTarget.lat},${driverTarget.lng}&travelmode=driving`} target="_blank" rel="noreferrer" className="rounded-full border border-surface-300 px-3 py-2 text-xs font-semibold text-ink-soft hover:bg-surface-100" data-testid="driver-open-maps">Google Maps</a>
                  </div>
                )}
                {isFood && (job.delivery_house_no || job.delivery_floor || job.delivery_landmark) && (
                  <p className="rounded-xl bg-brand-50 px-3 py-2 text-xs text-ink" data-testid="job-route-details">
                    <span className="font-semibold">Door details:</span> {[job.delivery_house_no, job.delivery_floor && `Floor ${job.delivery_floor}`, job.delivery_landmark].filter(Boolean).join(" · ")}
                  </p>
                )}
                {(job.delivery_note || job.pickup_note) && <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-ink" data-testid="job-note"><span className="font-semibold">{job.delivery_note ? "Directions:" : "Customer note:"}</span> {job.delivery_note || job.pickup_note}</p>}
                {!isFood && phase === "on_trip" && !endedRide && job.is_intercity && (
                  <div className="space-y-2 rounded-xl border border-brand-200 bg-brand-50 p-3" data-testid="intercity-charges">
                    <p className="text-xs font-bold text-brand">Add tolls &amp; waiting (intercity)</p>
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <label className="mb-1 block text-[11px] font-semibold text-ink-muted">Toll (₹)</label>
                        <Input value={tollInput} onChange={(e) => setTollInput(e.target.value.replace(/[^\d.]/g, ""))} inputMode="decimal" placeholder="0" className="h-10 bg-white" data-testid="intercity-toll-input" />
                      </div>
                      <div className="flex-1">
                        <label className="mb-1 block text-[11px] font-semibold text-ink-muted">Waiting (min)</label>
                        <Input value={waitInput} onChange={(e) => setWaitInput(e.target.value.replace(/\D/g, ""))} inputMode="numeric" placeholder="0" className="h-10 bg-white" data-testid="intercity-wait-input" />
                      </div>
                    </div>
                    <p className="text-[11px] text-ink-muted">Waiting is billed at ₹2/min after the first 30 free minutes. Shown on the receipt.</p>
                    <Button onClick={saveIntercityCharges} disabled={chargesBusy} className="h-9 w-full rounded-full bg-brand text-white hover:bg-brand-600" data-testid="intercity-charges-save">{chargesBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Add to fare</Button>
                  </div>
                )}
                {!isFood && phase === "to_pickup" ? (
                  <div className="space-y-2" data-testid="ride-start-code-entry">
                    {job.purpose === "goods" && (
                      <div className="rounded-xl border border-amber-200 bg-amber-50 p-3" data-testid="goods-pay-gate">
                        {(job.payment_method || "cash") !== "cash" && job.payment_status === "paid" ? (
                          <p className="flex items-center gap-1.5 text-xs font-bold text-green-700" data-testid="goods-prepaid"><Smartphone className="h-3.5 w-3.5" /> Customer Paid Online · {inr2(job.fare)} ✓</p>
                        ) : job.cash_collected ? (
                          <p className="flex items-center gap-1.5 text-xs font-bold text-emerald-700" data-testid="goods-cash-done"><CheckCircle2 className="h-4 w-4" /> Customer Paid Cash · estimated collected ✓</p>
                        ) : (
                          <div className="space-y-2">
                            <p className="text-xs font-semibold text-amber-800">Collect {inr2(job.fare)} (estimated) cash from the sender before you pick up the package.</p>
                            <Button onClick={collectGoodsCashPickup} className="h-10 rounded-full bg-amber-600 text-white hover:bg-amber-700" data-testid="goods-collect-cash-pickup"><Banknote className="mr-2 h-4 w-4" /> Cash received</Button>
                          </div>
                        )}
                      </div>
                    )}
                    <p className="text-xs text-ink-soft">Ask the rider for their 4-digit ride code and enter it to start the trip.</p>
                    <div className="flex gap-2">
                      <Input value={startCode} onChange={(e) => setStartCode(e.target.value.replace(/\D/g, "").slice(0, 4))} inputMode="numeric" maxLength={4} placeholder="4-digit code" className="h-11 flex-1 text-center text-lg tracking-[0.3em]" data-testid="start-code-input" />
                      <Button onClick={startRideTrip} disabled={startCode.length !== 4 || (job.purpose === "goods" && job.payment_status !== "paid" && !job.cash_collected)} className="h-11 rounded-full bg-brand text-white hover:bg-brand-600" data-testid="start-trip"><Navigation className="mr-2 h-4 w-4" /> Start trip</Button>
                      <Button variant="ghost" onClick={cancelJob} className="h-11 rounded-full text-red-700 hover:bg-red-50" data-testid="job-cancel"><X className="h-4 w-4" /></Button>
                    </div>
                  </div>
                ) : endedRide && !isFood ? (
                  <div className="space-y-2 rounded-xl bg-surface-100 p-3" data-testid="driver-ride-payment">
                    <p className="text-sm font-semibold text-ink">At drop · collect {inr2(endedRide.fare)}</p>
                    {endedRide.fare_reconciled && <p className="rounded-lg bg-amber-50 px-2 py-1.5 text-xs font-medium text-amber-700" data-testid="driver-reconcile-note">{endedRide.reconcile_note}</p>}
                    {endedRide.purpose === "goods" && (
                      <div className="space-y-1.5 rounded-lg border border-brand-200 bg-brand-50 p-2.5" data-testid="driver-dropcode-block">
                        <p className="text-xs font-semibold text-ink">Enter the receiver's 4-digit drop code</p>
                        <input inputMode="numeric" maxLength={4} value={dropCode} onChange={(e) => setDropCode(e.target.value.replace(/\D/g, ""))} disabled={codeOverride} placeholder="••••" className="w-full rounded-lg border border-surface-300 bg-white px-3 py-2 text-center text-lg font-bold tracking-[0.3em] disabled:opacity-40" data-testid="driver-dropcode-input" />
                        <label className="flex items-center gap-2 text-xs text-ink-soft"><input type="checkbox" checked={codeOverride} onChange={(e) => setCodeOverride(e.target.checked)} data-testid="driver-dropcode-override" /> Receiver unavailable — complete with photo &amp; signature proof</label>
                        {codeOverride && !(endedRide.proof_photo_id || endedRide.proof_signature_id) && <p className="text-[11px] font-semibold text-red-600">Capture a delivery photo or signature above first.</p>}
                      </div>
                    )}
                    {endedRide.payment_status === "paid" || endedRide.cash_collected ? (
                      <>
                        <p className="flex items-center gap-1.5 text-xs font-semibold text-emerald-700" data-testid="driver-ride-settled"><CheckCircle2 className="h-4 w-4" /> {(endedRide.payment_method || "cash") !== "cash" && endedRide.payment_status === "paid" ? "Customer Paid Online" : "Customer Paid Cash"}</p>
                        <Button onClick={() => finalizeRide(false)} disabled={payBusy || (endedRide.purpose === "goods" && !(dropCode.trim().length === 4 || codeOverride))} className="w-full rounded-full bg-brand text-white hover:bg-brand-600 disabled:opacity-50" data-testid="driver-complete-ride">{payBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PackageCheck className="mr-2 h-4 w-4" />} Complete trip</Button>
                      </>
                    ) : (
                      <>
                        <Button onClick={markCashCollected} disabled={payBusy || endedRide.payment_method === "razorpay"} className="w-full rounded-full bg-graphite text-white hover:bg-graphite-600 disabled:opacity-50" data-testid="driver-collect-cash-complete"><Banknote className="mr-2 h-4 w-4" /> Cash Collected</Button>
                        <p className="text-[11px] text-ink-soft">{endedRide.payment_method === "razorpay" ? "Rider is paying online — the moment their payment lands you'll see it here and can complete the trip." : "Collect the cash fare, then tap Cash Collected."}</p>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button onClick={advance} className="flex-1 rounded-full bg-brand text-white hover:bg-brand-600" data-testid="job-advance">
                      {phase === "to_pickup" ? <Navigation className="mr-2 h-4 w-4" /> : <PackageCheck className="mr-2 h-4 w-4" />}
                      {isFood ? (phase === "to_pickup" ? "Picked up order" : "Mark delivered") : "Reached drop location"}
                    </Button>
                    {(job.rider_phone) && <Button asChild variant="outline" className="rounded-full"><a href={`tel:${job.rider_phone}`}><Phone className="h-4 w-4" /></a></Button>}
                    {isFood ? (
                      <Button variant="ghost" onClick={cancelJob} className="rounded-full text-red-700 hover:bg-red-50" data-testid="job-cancel"><X className="h-4 w-4" /></Button>
                    ) : (
                      <span className="rounded-full bg-amber-50 px-3 py-2 text-[11px] font-medium text-amber-700" data-testid="job-inprogress-support">Trip started — contact support to cancel/complete</span>
                    )}
                  </div>
                )}
              </div>
            </Section>
            {queuedJob ? (
              <Section title="Next job lined up" actions={<KindBadge kind={queuedJob.kind} />}>
                <div className="space-y-2" data-testid="queued-job">
                  <p className="text-sm text-ink-soft">You've pre-accepted your next job. Finish your current trip and it'll start automatically.</p>
                  <Route2 a={queuedJob.pickup_address} b={queuedJob.drop_address} aLabel={queuedJob.kind === "food" ? "Restaurant" : "Pickup"} bLabel={queuedJob.kind === "food" ? "Customer" : "Drop"} />
                  <p className="text-sm font-bold text-ink">{inr2(queuedJob.fare)}</p>
                </div>
              </Section>
            ) : incoming ? (
              <Section title={incoming.kind === "food" ? "New delivery — accept for later?" : "New ride — accept for later?"} description="Line it up for after your current trip" actions={<KindBadge kind={incoming.kind} />}>
                <div className="space-y-3" data-testid="incoming-next-request">
                  <Route2 a={incoming.pickup_address} b={incoming.drop_address} aLabel={incoming.kind === "food" ? "Restaurant" : "Pickup"} bLabel={incoming.kind === "food" ? "Customer" : "Drop"} />
                  <p className="text-sm font-bold text-ink">{inr2(incoming.fare)}</p>
                  <div className="flex gap-2">
                    <Button onClick={accept} className="flex-1 rounded-full bg-graphite text-white hover:bg-graphite-600" data-testid="request-accept-next"><Check className="mr-2 h-4 w-4" /> Accept next</Button>
                    <Button onClick={decline} variant="outline" className="rounded-full" data-testid="request-decline-next"><X className="mr-2 h-4 w-4" /> Skip</Button>
                  </div>
                </div>
              </Section>
            ) : null}
            </>
          ) : incoming ? (
            <Section title={isFood ? "New delivery request" : "New ride request"} description="Respond within a few seconds" actions={<KindBadge kind={incoming.kind} />}>
              <div className="space-y-4" data-testid="incoming-request" data-kind={incoming.kind}>
                {isFood ? (
                  <>
                    <div className="flex items-center gap-3">
                      {incoming.restaurant_image ? <img src={incoming.restaurant_image} alt="" className="h-12 w-12 rounded-xl object-cover" /> : <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand"><Store className="h-5 w-5" /></span>}
                      <div className="min-w-0 flex-1"><p className="truncate text-sm font-semibold text-ink">{incoming.restaurant_name}</p><p className="text-xs text-ink-soft">{incoming.items?.length ? `${incoming.items.reduce((s, i) => s + i.qty, 0)} items` : "Order"} · {incoming.payment === "cod" ? "Cash on delivery" : "Prepaid"}</p></div>
                      <p className="text-xl font-bold text-ink">{inr2(incoming.fare)}</p>
                    </div>
                    <Route2 a={incoming.pickup_address} b={incoming.drop_address} aLabel="Pick up at" bLabel="Deliver to" />
                    <p className="text-xs text-ink-muted">Payout {inr2(incoming.fare)} · {incoming.payment === "cod" ? `collect ${inr2(incoming.order_value)} cash` : "prepaid"}</p>
                  </>
                ) : (
                  <>
                    <div className="flex items-center justify-between"><div><p className="text-sm font-semibold text-ink">{incoming.rider_name}</p><p className="inline-flex items-center gap-1 text-xs text-ink-soft"><Star className="h-3 w-3 fill-brand text-brand" />{incoming.rider_rating}</p></div><p className="text-xl font-bold text-ink">{inr2(incoming.fare)}</p></div>
                    {incoming.purpose === "goods" && <p className="flex items-center gap-1.5 text-xs font-bold text-brand" data-testid="incoming-goods"><Package className="h-3.5 w-3.5" /> Goods delivery{incoming.weight_kg ? ` · ${incoming.weight_kg} kg` : ""} — you don't load/unload</p>}
                    {incoming.rider_noshow_flag && <p className="flex items-center gap-1.5 rounded-xl bg-amber-50 px-3 py-2 text-xs font-bold text-amber-800" data-testid="rider-noshow-flag"><AlertTriangle className="h-3.5 w-3.5" /> Heads-up: this rider has {incoming.rider_noshow_count} past no-shows</p>}
                    {incoming.rider_reliability && (
                      <p className={`inline-flex w-fit items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold ${incoming.rider_reliability.label === "new" ? "bg-surface-200 text-ink-soft" : incoming.rider_reliability.label === "good" ? "bg-green-100 text-green-700" : incoming.rider_reliability.label === "watch" ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-700"}`} data-testid="rider-reliability">
                        <BadgeCheck className="h-3.5 w-3.5" /> {incoming.rider_reliability.label === "new" ? "New rider" : incoming.rider_reliability.trusted ? `Trusted · ${incoming.rider_reliability.score}% on-time` : `On-time ${incoming.rider_reliability.score}%`}
                      </p>
                    )}
                    <Route2 a={incoming.pickup_address} b={incoming.drop_address} aLabel="Pickup" bLabel="Drop" />
                    <p className="text-xs text-ink-muted">{incoming.distance_km} km trip</p>
                    {incoming.ac_requested && <p className="flex items-center gap-1.5 text-xs font-bold text-sky-600" data-testid="incoming-ac"><Snowflake className="h-3.5 w-3.5" /> AC ride{incoming.ac_broadcast_all ? " · sent to all cars (AC charge only if you have AC)" : " · includes 20% AC add-on"}</p>}
                  </>
                )}
                {(incoming.delivery_house_no || incoming.delivery_floor || incoming.delivery_landmark) && <p className="rounded-xl bg-brand-50 px-3 py-2 text-xs text-ink" data-testid="request-route-details"><span className="font-semibold">Door details:</span> {[incoming.delivery_house_no, incoming.delivery_floor && `Floor ${incoming.delivery_floor}`, incoming.delivery_landmark].filter(Boolean).join(" · ")}</p>}
                {(incoming.delivery_note || incoming.pickup_note) && <p className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-ink" data-testid="request-note"><span className="font-semibold">{incoming.delivery_note ? "Directions:" : "Customer note:"}</span> {incoming.delivery_note || incoming.pickup_note}</p>}
                <div className="flex gap-2"><Button onClick={accept} className="flex-1 rounded-full bg-graphite text-white hover:bg-graphite-600" data-testid="request-accept"><Check className="mr-2 h-4 w-4" /> Accept</Button><Button onClick={decline} variant="outline" className="rounded-full" data-testid="request-decline"><X className="mr-2 h-4 w-4" /> Skip</Button></div>
              </div>
            </Section>
          ) : (
            <Section title={online ? "Looking for requests" : "You are offline"}>
              <div className="flex flex-col items-center py-6 text-center" data-testid="idle-state">
                <span className={`relative flex h-16 w-16 items-center justify-center rounded-full ${online ? "bg-graphite text-white kt-pulse-ring" : "bg-surface-200 text-ink-muted"}`}>{online ? <Loader2 className="h-7 w-7 animate-spin" /> : <Power className="h-7 w-7" />}</span>
                <p className="mt-4 text-sm text-ink-soft">{online ? `${effectiveMode === "food" ? "Delivery" : effectiveMode === "both" ? "Ride and delivery" : "Ride"} requests near your location will appear here.` : verified ? "Switch online to start receiving requests." : "Complete verification, then switch online."}</p>
                <p className="mt-2 inline-flex items-center gap-1 text-xs text-ink-muted"><MapPin className="h-3 w-3" />{pos.lat.toFixed(4)}, {pos.lng.toFixed(4)}</p>
              </div>
              {online && <SampleRequestCard mode={effectiveMode === "food" ? "food" : "rides"} />}
            </Section>
          )}
          <Section title="Recent earnings" actions={<Button asChild variant="ghost" className="rounded-full text-brand hover:bg-brand-50"><Link to="/app/earnings">View all</Link></Button>}>
            {recent.length === 0 ? <p className="text-sm text-ink-muted">No trips logged yet.</p> : (
              <ul className="divide-y divide-surface-200 text-sm" data-testid="recent-earnings">{recent.slice(0, 5).map((r, i) => <li key={r.id || i} className="flex items-center justify-between py-2.5"><span className="inline-flex items-center gap-2 text-ink">{r.kind === "food" ? <UtensilsCrossed className="h-3.5 w-3.5 text-brand" /> : <Car className="h-3.5 w-3.5 text-graphite" />}{r.kind === "food" ? "Food delivery" : "Ride"}{r.distance_km ? ` · ${r.distance_km} km` : ""}</span><span className="font-semibold text-ink">{inr2(r.amount)}</span></li>)}</ul>
            )}
          </Section>
        </div>
        <div className="relative">
          <RideMap
            pickup={focus ? { lat: focus.pickup_lat, lng: focus.pickup_lng } : null}
            drop={focus ? { lat: focus.drop_lat, lng: focus.drop_lng } : null}
            route={job ? (driverRoad.points.length >= 2 ? driverRoad.points : [pos, driverTarget]) : null}
            traffic={job ? driverRoad.traffic : []}
            activeDriver={job ? { id: "me", lat: pos.lat, lng: pos.lng, vehicle_type: user?.vehicle_type || "car", name: "You" } : null}
            drivers={job ? [] : [{ id: "me", lat: pos.lat, lng: pos.lng, vehicle_type: user?.vehicle_type || "car" }]}
            heatmapPoints={!job && online ? heatPoints : null}
            heatOpacity={0.25 + 0.4 * (forecast.confidence ?? 1)}
            className="min-h-[420px] lg:min-h-[640px]"
          />
          {!job && online && forecast.busy_soon && (
            <button
              type="button"
              onClick={() => { const h = forecast.hotspot; if (h) window.open(`https://www.google.com/maps/dir/?api=1&destination=${h.lat},${h.lng}&travelmode=driving`, "_blank", "noopener"); }}
              className="absolute right-4 top-4 z-[500] inline-flex items-center gap-1.5 rounded-full bg-brand px-3 py-1.5 text-xs font-bold text-white shadow-lg kt-pulse-ring transition-transform hover:scale-105 active:scale-95"
              data-testid="driver-busy-soon-badge"
              title="Tap for directions to the hotspot"
            >
              <Clock className="h-3.5 w-3.5" /> Busy soon{forecast.nearest_hotspot_km != null ? ` · ${forecast.nearest_hotspot_km} km` : ""}
              <Navigation className="h-3.5 w-3.5" />
            </button>
          )}
          {!job && online && heatPoints.length > 0 && (
            <div className="absolute left-4 top-4 z-[500] flex max-w-[280px] flex-col gap-1.5">
              <div className="rounded-full bg-graphite/90 px-3 py-1.5 text-xs font-semibold text-white" data-testid="driver-demand-legend">{forecast.confidence < 0.4 ? "Predicted demand · next hour — limited history, strong zones only" : "Predicted demand · next hour — pre-position in the hot zones"}</div>
              {forecast.extra_earnings > 0 && (
                <div className="inline-flex items-center gap-1.5 self-start rounded-full bg-green-600/95 px-3 py-1.5 text-xs font-bold text-white" data-testid="driver-earnings-forecast">
                  <Banknote className="h-3.5 w-3.5" /> ~₹{forecast.extra_earnings} extra likely if you pre-position this hour
                </div>
              )}
            </div>
          )}
          {!job && online && timeline.length > 0 && (() => {
            const maxC = Math.max(1, ...timeline.map((s) => s.count));
            return (
              <div className="absolute bottom-4 left-4 right-4 z-[500] rounded-2xl bg-white/95 p-3 shadow-lg backdrop-blur" data-testid="driver-zone-timeline">
                <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-ink-muted">Busy hours today{timelinePeak != null ? ` · peak around ${((timelinePeak % 12) || 12)}${timelinePeak < 12 ? "AM" : "PM"}` : ""}</p>
                <div className="flex items-end justify-between gap-1.5" style={{ height: 56 }}>
                  {timeline.map((s) => (
                    <div key={s.hour} className="flex flex-1 flex-col items-center gap-1">
                      <div className={`w-full rounded-t ${s.hour === timelinePeak ? "bg-brand" : "bg-graphite/30"}`} style={{ height: `${Math.max(6, (s.count / maxC) * 40)}px` }} />
                      <span className="text-[9px] font-semibold text-ink-muted">{s.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}
          {job && driverRoad.traffic.length > 0 && (
            <div className="absolute right-4 top-4 z-[500]"><TrafficLegend /></div>
          )}
        </div>
      </div>
      <RingModal
        open={!!incoming && !job}
        accent="graphite"
        title={isFood ? "New delivery request!" : "New ride request!"}
        subtitle={incoming ? `${inr2(incoming.fare)} payout` : ""}
        acceptLabel="Accept"
        rejectLabel="Skip"
        onAccept={accept}
        onReject={decline}
      >
        {incoming && (isFood ? (
          <div className="space-y-2">
            <p className="text-sm font-semibold text-ink">{incoming.restaurant_name}</p>
            <Route2 a={incoming.pickup_address} b={incoming.drop_address} aLabel="Pick up at" bLabel="Deliver to" />
            <p className="text-xs text-ink-muted">Payout {inr2(incoming.fare)} · {incoming.payment === "cod" ? `collect ${inr2(incoming.order_value)} cash` : "prepaid"}</p>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center justify-between"><p className="text-sm font-semibold text-ink">{incoming.rider_name}</p><p className="inline-flex items-center gap-1 text-xs text-ink-soft"><Star className="h-3 w-3 fill-brand text-brand" />{incoming.rider_rating}</p></div>
            <Route2 a={incoming.pickup_address} b={incoming.drop_address} aLabel="Pickup" bLabel="Drop" />
            <p className="text-xs text-ink-muted">{incoming.distance_km} km trip · {incoming.distance_to_pickup_km} km to pickup</p>
          </div>
        ))}
      </RingModal>
      {job && job.purpose === "goods" && (
        <GoodsProofDialog open={proofOpen} rideId={job.ride_id} onClose={() => setProofOpen(false)} onDone={async () => { setProofOpen(false); const jr = await ridesApi.get(job.ride_id).catch(() => null); if (jr) setJob(jr); }} />
      )}
      <DriverCancelDialog open={cancelReasonOpen} onOpenChange={setCancelReasonOpen} onConfirm={submitCancelJob} />
      {navDest && <InAppNav destination={navDest} label={phase === "to_pickup" ? "pickup point" : "drop-off"} onExit={() => setNavDest(null)} />}
    </>
  );
}


const DRIVER_CANCEL_REASONS = ["Too far from me", "Vehicle issue", "Personal emergency", "Can't reach rider", "Other"];

function DriverCancelDialog({ open, onOpenChange, onConfirm }) {
  const [reason, setReason] = useState(null);
  const [note, setNote] = useState("");
  useEffect(() => { if (open) { setReason(null); setNote(""); } }, [open]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="driver-cancel-dialog">
        <DialogHeader>
          <DialogTitle>Cancel this ride?</DialogTitle>
          <DialogDescription>It'll go back to searching for another driver — the rider isn't charged. Tell the rider why.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-wrap gap-2">
          {DRIVER_CANCEL_REASONS.map((r) => (
            <button key={r} type="button" onClick={() => setReason(reason === r ? null : r)} data-testid={`driver-cancel-reason-${r.replace(/[^a-z]+/gi, "-").toLowerCase().replace(/-+$/, "")}`} className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${reason === r ? "border-brand bg-brand text-white" : "border-surface-300 bg-white text-ink-soft hover:border-brand-200"}`}>{r}</button>
          ))}
        </div>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} maxLength={280} placeholder="Add a note (optional)" className="mt-1" data-testid="driver-cancel-note" />
        <div className="mt-2 flex gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1 rounded-full" data-testid="driver-cancel-keep">Keep ride</Button>
          <Button onClick={() => onConfirm(reason, note.trim())} className="flex-1 rounded-full bg-red-600 text-white hover:bg-red-700" data-testid="driver-cancel-confirm">Cancel ride</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
